import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { PurchaseNavButton } from './CheckoutFlow';

const PAGES = {
  privacy: {
    title: 'Privacy Policy',
    updated: 'August 2026',
    sections: [
      {
        heading: 'What this covers',
        body: 'This policy describes how ImmigrateSmart collects and uses information when you use our website and pathway tools. It is a product notice, not legal advice.',
      },
      {
        heading: 'Information we collect',
        body: 'Account details (name, email, password hash). Immigration profile and workflow answers you enter. Generated documents and evidence files you upload. Payment records processed by Stripe (we do not store full card numbers). Basic device and usage logs needed to operate the service.',
      },
      {
        heading: 'How we use it',
        body: 'To create your account, unlock purchased pathways, pre-populate USCIS form drafts, generate supporting documents, and provide customer support. We do not sell your personal information. Anonymised case outcomes may be used for community statistics unless you opt out in the product.',
      },
      {
        heading: 'Payments',
        body: 'Checkout is handled by Stripe. Stripe receives the information needed to process one-time payments. See Stripe’s own privacy policy for their processing.',
      },
      {
        heading: 'Retention',
        body: 'Generated documents expire after 60 days. Account, profile, and workflow data are kept while your account is active so you can resume work. You may request deletion of your account data by contacting us.',
      },
      {
        heading: 'Contact',
        body: 'Questions about this policy can be sent through the consultation form on the site.',
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    updated: 'August 2026',
    sections: [
      {
        heading: 'The service',
        body: 'ImmigrateSmart provides self-help immigration workflow software: eligibility information, form drafts, and supporting documents. It is not a law firm and does not create an attorney-client relationship unless you separately engage counsel.',
      },
      {
        heading: 'Purchases',
        body: 'Pathway access is a one-time purchase for that pathway, billed through Stripe. Prices are shown at checkout. Add-ons marked coming soon are not available for purchase. You are responsible for reviewing all generated documents before filing with USCIS or any agency.',
      },
      {
        heading: 'Your responsibilities',
        body: 'Provide accurate information. Do not use the service for anyone else’s filing without authority. Keep your login credentials secure. USCIS rules change; you must confirm current instructions, fees, and filing locations before you file.',
      },
      {
        heading: 'No guarantee of outcome',
        body: 'We do not guarantee approval, processing times, or that a draft will be accepted by USCIS. Case-strength scores and checklists are informational aids only.',
      },
      {
        heading: 'Limitation',
        body: 'To the extent allowed by law, ImmigrateSmart is not liable for filing denials, missed deadlines, or damages arising from your use of drafts generated by the service. Some jurisdictions do not allow certain limitations; those limits apply only as permitted.',
      },
    ],
  },
  disclaimer: {
    title: 'Not legal advice',
    updated: 'August 2026',
    sections: [
      {
        heading: 'Legal information only',
        body: 'ImmigrateSmart provides general legal information and document-preparation tools. It is not a substitute for advice from a licensed immigration attorney about your facts.',
      },
      {
        heading: 'No attorney-client relationship',
        body: 'Using this website, buying a pathway, or requesting a consultation through the form does not by itself create an attorney-client relationship. Attorney draft review, when offered, is a separate purchase and engagement.',
      },
      {
        heading: 'Drafts are not filed forms',
        body: 'PDFs and DOCX files are pre-populated drafts from the answers you typed. Compare them to the official USCIS form, instructions, and current filing fees before you sign or mail anything.',
      },
      {
        heading: 'Get counsel when you need it',
        body: 'If you have a criminal record, prior removal, an RFE or NOID, a tight deadline, or an unusual family or employment fact pattern, speak with a licensed attorney before you file.',
      },
    ],
  },
};

export default function LegalPage() {
  const { page } = useParams();
  const content = PAGES[page] || PAGES.disclaimer;

  return (
    <div className="min-h-screen bg-[#F4F3F0]">
      <nav className="bg-[#3A3A3F] px-4 sm:px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center text-white hover:opacity-90">
            <span className="wordmark-mark" aria-hidden="true"></span>
            <span className="wordmark text-lg text-white">ImmigrateSmart</span>
          </a>
          <div className="flex items-center gap-3">
            <PurchaseNavButton />
            <a href="/eligibility" className="text-white/80 hover:text-[#D4A017] text-sm">
              Eligibility
            </a>
          </div>
        </div>
      </nav>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <p className="text-[10px] tracking-[0.16em] uppercase text-[#B8860B] font-semibold mb-2">
          Updated {content.updated}
        </p>
        <h1 className="font-cormorant text-4xl text-[#222226] mb-8">{content.title}</h1>
        <div className="space-y-8">
          {content.sections.map((s) => (
            <section key={s.heading}>
              <h2 className="text-lg font-semibold text-[#222226] mb-2">{s.heading}</h2>
              <p className="text-[#3F3C38] text-sm leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>
        <p className="mt-12 text-sm text-[#3F3C38]/70">
          <Link to="/" className="text-[#B8860B] hover:underline">Back to home</Link>
          {' · '}
          <Link to="/legal/privacy" className="hover:underline">Privacy</Link>
          {' · '}
          <Link to="/legal/terms" className="hover:underline">Terms</Link>
          {' · '}
          <Link to="/legal/disclaimer" className="hover:underline">Not legal advice</Link>
        </p>
      </main>
    </div>
  );
}
